/**
 * Composition hook for the sidebar / drawer advisor list.
 *
 * HORO-10426:
 *   - Anonymous users → a FIXED top-5 roster (the same five as the mobile
 *     homepage carousel: Veda, Ophelia, Atlas, Avalon, Estelle from
 *     HOME_TOP_ADVISORS). Marketing-forward, consistent for first-time visitors.
 *   - Logged-in users → the original recents-based list: backend
 *     `recentAdvisorAliases` (most-recent first) padded with
 *     DEFAULT_ADVISOR_LIST, up to 6. Keeps tapping a recent advisor opening a
 *     greeting chat (returning-advisor path in the chat page) rather than the
 *     Tarot/Horoscope intro cards.
 *
 * HORO-10504 / HORO-10581: whenever ZARA_ENABLED, Zara is pinned to the FRONT
 * of either list (ahead of the anon roster and ahead of recents) — she's
 * discoverable here regardless of whether the viewer has ever chatted with
 * her, which is the whole point of pinning the subscription upsell. Still
 * counts against the same MAX_LIST cap rather than adding an extra row, since
 * MenuContent's fit-to-height math is keyed off row count. Deduped like
 * everything else here, so an already-recent Zara doesn't appear twice.
 *
 * The pin used to be time-boxed to a launch window (ZARA_FEATURED_PIN_UNTIL,
 * 2026-09-19) on the assumption that she'd "only appear via normal recency"
 * afterwards. She doesn't: neither fallback roster below contains her, and an
 * anonymous viewer has no recents at all — so that date passing silently
 * dropped her out of this list on every environment. HORO-10581 removed the
 * date; ZARA_ENABLED is now the only switch.
 *
 * The pin also requires Zara to actually be present in the live catalogue
 * (`advisorsByAlias`) — ZARA_ENABLED can easily be on in an environment/region
 * before the backend has actually enabled her there, or before the catalogue
 * has loaded at all. Pinning a row for an advisor that doesn't really exist
 * there would be a dead link (backend 404s her) with no real name/avatar
 * behind it — worse than just not pinning, so this falls back to the plain
 * recents list. Recomputes once the catalogue finishes loading
 * (advisorsByAlias is already a dependency below), so she appears the moment
 * she's confirmed real rather than never.
 *
 * Each entry is hydrated with `name` / `expertise` / `avatar` from the
 * catalogue once it loads, falling back to static metadata so first paint
 * never blanks. (MenuContent trims the visible count to fit short viewports.)
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DEFAULT_ADVISOR_LIST, HOME_TOP_ADVISORS, ZARA_ENABLED } from '../../../const';
import { ZARA_ADVISOR_ALIAS } from '../../../service/advisorAccess';
import { getRecentAdvisorAliases, getUser } from '../../../store/slices/authSlice';
import { getAdvisorsByAlias } from '../../../store/slices/catalogSlice';

const MAX_LIST = 6;

// Static first-paint metadata for an alias, checked across every fallback
// roster this hook draws from. No Zara entry here (unlike the others, she's
// never used as this hook's OWN fallback) — she's only ever pinned once
// `advisorsByAlias` confirms she's real, at which point `fromCatalog` in
// `hydrate` below already has real name/expertise to use.
const staticFallbackFor = (alias) =>
    DEFAULT_ADVISOR_LIST.find((a) => a.alias === alias) || HOME_TOP_ADVISORS.find((a) => a.alias === alias);

// Hydrate an alias into a row, preferring live catalogue data over the
// static fallback. `images.avatar` is the small menu thumbnail; `chat` /
// `preview` are larger formats used as fallbacks.
const hydrate = (advisorsByAlias, alias) => {
    const fromCatalog = advisorsByAlias[alias];
    const fallback = staticFallbackFor(alias);
    return {
        alias,
        name: fromCatalog?.name || fallback?.name || alias,
        expertise: fromCatalog?.expertise || fallback?.expertise || '',
        avatar: fromCatalog?.images?.avatar || fromCatalog?.images?.chat || fromCatalog?.images?.preview || ''
    };
};

// `pinned` first, then `rest`, deduped and capped at MAX_LIST — shared
// between the anon and logged-in branches below.
const buildList = (pinned, rest) => {
    const merged = [];
    const seen = new Set();
    for (const alias of [...pinned, ...rest]) {
        if (alias && !seen.has(alias) && merged.length < MAX_LIST) {
            seen.add(alias);
            merged.push(alias);
        }
    }
    return merged;
};

export const useAdvisorList = () => {
    const user = useSelector(getUser);
    const backendRecents = useSelector(getRecentAdvisorAliases);
    // Read from the alias-keyed cache rather than `advisorsList` — the catalog
    // grid replaces `advisorsList` with the active category filter, which would
    // otherwise blank out sidebar avatars (HORO-10396). Also what confirms
    // Zara is actually live before pinning her — see the block comment above.
    const advisorsByAlias = useSelector(getAdvisorsByAlias);

    return useMemo(() => {
        const pinned = ZARA_ENABLED && advisorsByAlias[ZARA_ADVISOR_ALIAS] ? [ZARA_ADVISOR_ALIAS] : [];

        // Anonymous: fixed top-5, matching the homepage carousel (Zara
        // pinned ahead of it when enabled and live).
        if (!user?.id) {
            return buildList(
                pinned,
                HOME_TOP_ADVISORS.map((a) => a.alias)
            ).map((alias) => hydrate(advisorsByAlias, alias));
        }

        // Logged-in: Zara pinned ahead of recents (when enabled and live),
        // padded with defaults (dedupe so the same alias doesn't appear
        // twice), preserving recency order otherwise.
        return buildList(pinned, [...backendRecents, ...DEFAULT_ADVISOR_LIST.map((a) => a.alias)]).map((alias) =>
            hydrate(advisorsByAlias, alias)
        );
    }, [user?.id, backendRecents, advisorsByAlias]);
};
